import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import CouponApi from "../Api/CouponApi";
import CategoryApi from "../Api/CategoryApi";
import ProductApi from "../Api/ProductApi";
import usePermissions from "../hook/usePermissions";
import { toast } from "react-toastify";
import StandardDatePicker from "./StandardDatePicker";

const CouponFormLayer = () => {
    const { hasPermission } = usePermissions();
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const isView = location.pathname.includes("/view/");

    const [formData, setFormData] = useState({
        code: "",
        title: "",
        status: false,
        startDate: "",
        endDate: "",
        discountType: "percentage",
        discountValue: "",
        offerType: "all",
        categoryIds: [],
        productIds: [],
        minOrderAmount: "",
        totalLimit: ""
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);

    const discountTypeOptions = [
        { value: "percentage", label: "Percentage (%)" },
        { value: "fixed", label: "Fixed Amount (₹)" }
    ];

    const offerTypeOptions = [
        { value: "all", label: "All Products" },
        { value: "category", label: "Category Based" },
        { value: "product", label: "Product Based" }
    ];

    useEffect(() => {
        fetchMetadata();
        if (id) fetchDetails();
    }, [id]);

    const fetchMetadata = async () => {
        const catRes = await CategoryApi.getAll(0, 500, "");
        if (catRes.status) setCategories(catRes.response.data.map(c => ({ value: c._id, label: c.name })));

        const prodRes = await ProductApi.getAll(0, 1000, "");
        if (prodRes.status) setProducts(prodRes.response.data.map(p => ({ value: p._id, label: p.name })));
    };

    const fetchDetails = async () => {
        setLoading(true);
        const response = await CouponApi.getById(id);
        if (response.status) {
            const data = response.response.data;
            setFormData({
                ...data,
                startDate: data.startDate || "",
                endDate: data.endDate || ""
            });
        }
        setLoading(false);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === "discountValue" && formData.discountType === "percentage") {
            if (value !== "" && Number(value) > 100) {
                setFormData(prev => ({ ...prev, [name]: "100" }));
                return;
            }
        }
        setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

    const handleKeyDown = (e) => {
        // Block signs like + and - as well as 'e' for scientific notation
        if (["e", "E", "+", "-"].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleSelectChange = (selectedOption, { name }) => {
        const selectedVal = selectedOption ? selectedOption.value : "";
        setFormData(prev => {
            let updated = { ...prev, [name]: selectedVal };
            if (name === "discountType" && selectedVal === "percentage" && Number(prev.discountValue) > 100) {
                updated.discountValue = "100";
            }
            return updated;
        });
    };

    const handleMultiSelectChange = (selectedOptions, { name }) => {
        setFormData(prev => ({ ...prev, [name]: selectedOptions ? selectedOptions.map(o => o.value) : [] }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.code?.trim()) newErrors.code = "Coupon Code is Required.";
        if (!formData.title?.trim()) newErrors.title = "Coupon Title is Required.";
        if (!formData.startDate) newErrors.startDate = "Start Date is Required.";
        if (!formData.endDate) newErrors.endDate = "End Date is Required.";
        if (new Date(formData.startDate) > new Date(formData.endDate)) newErrors.endDate = "End date must be after start date.";
        if (!formData.discountValue || formData.discountValue <= 0) newErrors.discountValue = "Valid Discount Value is Required.";
        if (formData.discountType === "percentage" && formData.discountValue > 100) newErrors.discountValue = "Percentage cannot exceed 100.";
        if (!formData.totalLimit || formData.totalLimit < 0) newErrors.totalLimit = "Order Limit must be 0 or more.";

        if (formData.offerType === "category" && formData.categoryIds.length === 0) newErrors.categoryIds = "Please select at least one category.";
        if (formData.offerType === "product" && formData.productIds.length === 0) newErrors.productIds = "Please select at least one product.";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        const payload = {
            ...formData,
            discountValue: parseFloat(formData.discountValue),
            minOrderAmount: formData.minOrderAmount ? parseFloat(formData.minOrderAmount) : null,
            totalLimit: parseInt(formData.totalLimit)
        };

        const result = id ? await CouponApi.update(id, payload) : await CouponApi.create(payload);
        setLoading(false);
        if (result.status) navigate("/coupons");
    };

    return (
        <div className="card h-100 p-0 radius-12 shadow-sm border-0">
            <div className="card-header border-bottom bg-base py-20 px-24">
                <h6 className="text-primary-600 mb-0 font-bold">{isView ? "View" : id ? "Edit" : "Create"} Coupon</h6>
            </div>
            <div className="card-body p-24">
                <form onSubmit={handleSubmit} noValidate>
                    <div className="row gy-4">
                        {/* Basic Information */}
                        <div className="col-12"><h6 className="text-md border-bottom pb-8 mb-0">1. Basic Information</h6></div>
                        <div className="col-md-4">
                            <label className="form-label font-semibold">Coupon Code <span className="text-danger">*</span></label>
                            <input type="text" className="form-control radius-8" name="code" value={formData.code} onChange={handleChange} placeholder="e.g. SAVE20" disabled={isView} />
                            {errors.code && <small className="text-danger">{errors.code}</small>}
                        </div>
                        <div className="col-md-8">
                            <label className="form-label font-semibold">Title / Name <span className="text-danger">*</span></label>
                            <input type="text" className="form-control radius-8" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Summer Sale 20% Off" disabled={isView} />
                            {errors.title && <small className="text-danger">{errors.title}</small>}
                        </div>

                        {/* Validity & Status */}
                        <div className="col-12 mt-40"><h6 className="text-md border-bottom pb-8 mb-0">2. Timing & Eligibility</h6></div>
                        <div className="col-md-3">
                            <label className="form-label font-semibold">Start Date <span className="text-danger">*</span></label>
                            <StandardDatePicker
                                name="startDate"
                                value={formData.startDate}
                                onChange={handleChange}
                                disabled={isView}
                                enableTime={true}
                                placeholder="DD/MM/YYYY hh:mm aa"
                            />
                            {errors.startDate && <small className="text-danger">{errors.startDate}</small>}
                        </div>
                        <div className="col-md-3">
                            <label className="form-label font-semibold">End Date <span className="text-danger">*</span></label>
                            <StandardDatePicker
                                name="endDate"
                                value={formData.endDate}
                                onChange={handleChange}
                                disabled={isView}
                                enableTime={true}
                                placeholder="DD/MM/YYYY hh:mm aa"
                            />
                            {errors.endDate && <small className="text-danger">{errors.endDate}</small>}
                        </div>
                        <div className="col-md-3">
                            <label className="form-label font-semibold">Status</label>
                            <div className="form-check mt-8 d-flex align-items-center gap-2">
                                <input className="premium-checkbox" type="checkbox" name="status" checked={formData.status} onChange={handleChange} disabled={isView} id="statusCheck" />
                                <label className="form-check-label text-secondary-700 mb-0" htmlFor="statusCheck">{formData.status ? "Active" : "Inactive"}</label>
                            </div>
                        </div>

                        {/* Discount Configuration */}
                        <div className="col-12 mt-40"><h6 className="text-md border-bottom pb-8 mb-0">3. Discount Configuration</h6></div>
                        <div className="col-md-4">
                            <label className="form-label font-semibold">Discount Type <span className="text-danger">*</span></label>
                            <Select
                                name="discountType"
                                options={discountTypeOptions}
                                value={discountTypeOptions.find(o => o.value === formData.discountType)}
                                onChange={handleSelectChange}
                                styles={selectStyles(errors.discountType)}
                                isDisabled={isView}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label font-semibold">Discount Value ({formData.discountType === "percentage" ? "%" : "₹"}) <span className="text-danger">*</span></label>
                            <input type="number" className="form-control radius-8" name="discountValue" value={formData.discountValue} onChange={handleChange} onKeyDown={handleKeyDown} placeholder={formData.discountType === "percentage" ? "e.g. 20" : "e.g. 500"} disabled={isView} min="0" max={formData.discountType === "percentage" ? "100" : undefined} />
                            {errors.discountValue && <small className="text-danger">{errors.discountValue}</small>}
                        </div>

                        {/* Rules & Targets */}
                        <div className="col-12 mt-40"><h6 className="text-md border-bottom pb-8 mb-0">4. Rules & Targeting</h6></div>
                        <div className="col-md-4">
                            <label className="form-label font-semibold">Offer Type <span className="text-danger">*</span></label>
                            <Select
                                name="offerType"
                                options={offerTypeOptions}
                                value={offerTypeOptions.find(o => o.value === formData.offerType)}
                                onChange={handleSelectChange}
                                styles={selectStyles(errors.offerType)}
                                isDisabled={isView}
                            />
                        </div>
                        <div className="col-md-8">
                            {formData.offerType === "category" && (
                                <>
                                    <label className="form-label font-semibold">Select Categories <span className="text-danger">*</span></label>
                                    <Select
                                        isMulti
                                        name="categoryIds"
                                        options={categories}
                                        value={categories.filter(o => formData.categoryIds.includes(o.value))}
                                        onChange={handleMultiSelectChange}
                                        styles={selectStyles(errors.categoryIds)}
                                        isDisabled={isView}
                                        closeMenuOnSelect={false}
                                    />
                                    {errors.categoryIds && <small className="text-danger">{errors.categoryIds}</small>}
                                </>
                            )}
                            {formData.offerType === "product" && (
                                <>
                                    <label className="form-label font-semibold">Select Products <span className="text-danger">*</span></label>
                                    <Select
                                        isMulti
                                        name="productIds"
                                        options={products}
                                        value={products.filter(o => formData.productIds.includes(o.value))}
                                        onChange={handleMultiSelectChange}
                                        styles={selectStyles(errors.productIds)}
                                        isDisabled={isView}
                                        closeMenuOnSelect={false}
                                    />
                                    {errors.productIds && <small className="text-danger">{errors.productIds}</small>}
                                </>
                            )}
                        </div>

                        <div className="col-md-4">
                            <label className="form-label font-semibold">Min Order Amount (₹)</label>
                            <input type="number" className="form-control radius-8" name="minOrderAmount" value={formData.minOrderAmount} onChange={handleChange} onKeyDown={handleKeyDown} placeholder="Optional" disabled={isView} min="0" />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label font-semibold">Order Limit <span className="text-danger">*</span></label>
                            <input type="number" className="form-control radius-8" name="totalLimit" value={formData.totalLimit} onChange={handleChange} onKeyDown={handleKeyDown} placeholder="e.g. 3" disabled={isView} min="0" />
                            {errors.totalLimit && <small className="text-danger">{errors.totalLimit}</small>}
                        </div>
                    </div>

                    <div className="d-flex justify-content-end gap-3 mt-40 pt-24 border-top">
                        <Link to="/coupons" className="btn btn-outline-secondary px-32 radius-8 h-50-px d-flex align-items-center fw-bold text-md">Cancel</Link>
                        {!isView && (
                            <button type="submit" className="btn btn-primary radius-8 px-32 h-50-px btn-hover-green fw-bold text-md" disabled={loading}>
                                {loading ? "Saving..." : (id ? "Update Coupon" : "Create Coupon")}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CouponFormLayer;
